import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
    useRef,
} from 'react';
import {useTranslation} from 'react-i18next';
import {Step5ValueModel} from '../types';
import Summary from '../../shared/Summary';
import {validateStep5} from '../Validation';
import {validateToC, handleRequestErrors} from '../../shared/Validation';
import {FormErrorType} from "@sharedComponents/PantaForm/types";
import {getAllErrors} from "@sharedComponents/PantaForm/utils";
import Icon from "@sharedComponents/Icon";
import Panel from "@sharedComponents/Panel";
import Button from "@sharedComponents/Button";
import InputCheckbox from "@sharedComponents/InputCheckbox";
import Alert from "@sharedComponents/Alert";
import {Step5Props} from "@apps/CompanyRegistrations/RegisterFlow/Forms/ProducerForm/Step5/types";
import ButtonLinkBlock from "@sharedComponents/ButtonLinkBlock";

const Step5: FunctionComponent<Step5Props> = props => {
    const {t} = useTranslation()

    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])

    const termsRef = useRef<HTMLDivElement>(null)

    const allErrors = getAllErrors(formErrors)

    const [stepValues, setStepValues] = useState<Step5ValueModel>({
        terms: false,
    })

    const onChangeChkTerms = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked

        const termsText =
            termsRef && termsRef.current && termsRef.current.textContent

        setStepValues({
            ...stepValues,
            terms: value,
            termsText: termsText || '',
        })

        props.onChangeValues &&
        props.onChangeValues({terms: value, termsText})

        let errors = validateToC(value, formErrors)

        errors = validateToC(value, formErrors)

        setFormErrors(errors)
    }

    const nextButtonText = (
        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
            <span>{t('button.submit')}</span>
        </span>
    )

    useEffect(() => {
        handleRequestErrors(formErrors, props.requestErrors).then(errors => {
            setFormErrors(errors)
        })
    }, [props.requestErrors])

    const prevStep = () => {
        setFormErrors([])
        props.resetRequestErrors()
        props.prevStep && props.prevStep()
    }

    const onSubmit = () => {
        validateStep5(stepValues, []).then((errors: FormErrorType[]) => {
            if (errors && errors.length > 0) {
                setFormErrors(errors)
            } else {
                setFormErrors([])

                props.onSubmit && props.onSubmit()
            }
        })
    }

    const onCancel = () => {
        setFormErrors([])
        props.onCancel && props.onCancel()
    }

    const cancelButtonText = (
        <span className='buttons-wrapper__button-text'>
            <Icon iconName="close"/>
            <span>{t('button.cancel')}</span>
        </span>
    )

    const renderButtonLinkBlocks = (props: Step5Props) => {
        if (props.termsAndConditionsButtons && props.termsAndConditionsButtons.length > 0) {
            return props.termsAndConditionsButtons.map(buttonLinkBlock =>
                <ButtonLinkBlock key={buttonLinkBlock.ContentLink.Expanded?.Id} {...buttonLinkBlock.ContentLink.Expanded} />
            );
        }

        return;
    }

    const optionalConnection = !!props.values?.optionalConnection
    const termsHtml = optionalConnection ? (
        <div className={"form-step-wrapper__terms-wrapper-text"} ref={termsRef}>
            <div dangerouslySetInnerHTML={{__html: props.alternativeTermsAndConditions}} />
            {renderButtonLinkBlocks(props)}
        </div>
    ) : (
        <div className={"form-step-wrapper__terms-wrapper-text"} ref={termsRef}>
            <div dangerouslySetInnerHTML={{__html: props.termsAndConditions}} />
            {renderButtonLinkBlocks(props)}
        </div>
    )

    return (
        <div className='producer-form form-step-wrapper form-step-wrapper--producer-form-step-5'>
            <h3>{t('companyRegistration.stepLabel.step5')}</h3>
            <div className='producer-form__panel-sub-section'>
                <Panel className="panel__preview">
                    <Summary sections={props.getSections(props.values)}/>
                    <div className='form-step-wrapper__button-wrapper'>
                        <Button
                            type="primary"
                            className="submit-button"
                            text={t('global.change')}
                            disabled={props.submitting}
                            onClick={prevStep}
                        />
                    </div>
                </Panel>
            </div>
            <div className='producer-form__panel-sub-section'>
                <Panel className="panel-terms">
                    <div className='form-step-wrapper__terms-wrapper'>
                        <InputCheckbox
                            id="optional-connection"
                            label={termsHtml}
                            onChange={onChangeChkTerms}
                            checked={Boolean(stepValues.terms)}
                            disableClickLabel={true}
                        />
                    </div>
                </Panel>
            </div>
            {allErrors.length > 0 && (
                <Alert
                    type="danger"
                    text={t('companyRegistration.companyInfoContainsErrors')}
                    subtext={allErrors}
                    compact={false}
                />
            )}
            {props.unexpectedErrors.length > 0 && (
                <Alert
                    type="danger"
                    text={''}
                    subtext={props.unexpectedErrors}
                    compact={true}
                />
            )}
            <div className='buttons-wrapper'>
                <Button
                    type="primary"
                    className="next-button"
                    text={nextButtonText}
                    onClick={onSubmit}
                    disabled={!stepValues.terms || props.submitting}
                    loading={props.submitting}
                />
                <Button
                    type="outlined"
                    className="cancel-button"
                    text={cancelButtonText}
                    disabled={props.submitting}
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step5;